import { MIN_SPLIT_POPULATION } from '../insights/bucketStats';

// Lift is how much better a node converts than the dataset as a whole. It is the
// number that answers "is this node worth anything", which a bare conversion rate
// does not: 12% is strong on a 3% baseline and poor on a 20% one.
//
// Shared by the tree sidebar and the nodes table so the two cannot disagree, and
// computed against the same denominator the AI model summary uses (the training
// baseline, i.e. the root node's rate) so the tree page and the summary agree too.

/** Conversion rate of the whole dataset, taken from the tree's root node. */
export function baselineRate(root?: {
  population?: number;
  conversions?: number;
}): number | null {
  if (!root?.population) return null;
  const rate = Number(root.conversions) / Number(root.population);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

/**
 * Node rate over baseline rate. Null when there is no usable baseline, so callers
 * render nothing rather than a made up multiple.
 */
export function liftVsBaseline(
  rate: number,
  baseline: number | null
): number | null {
  if (!baseline || !Number.isFinite(rate)) return null;
  const lift = rate / baseline;
  return Number.isFinite(lift) ? lift : null;
}

/** "2.9x", matching how the AI model summary writes the same figure. */
export function formatLift(lift: number | null): string {
  return lift === null ? '' : `${lift.toFixed(1)}x`;
}

/**
 * Bar width as a percentage. Capped at 4x so one outlier node does not flatten
 * every other bar into invisibility; the number beside it still reads exactly.
 */
export function liftBarPercent(lift: number | null): number {
  if (lift === null || lift <= 0) return 0;
  return Math.min(100, (lift / 4) * 100);
}

// ---------------------------------------------------------------------------
// Split separation: what an INTERNAL node is worth.
//
// Lift is the wrong question for a node that is not a leaf. A node's own lift
// says how good the leads that reach it are, which is a property of the path
// above it, not of the cut it makes. A node sitting at 3x can carry a split that
// sends 3.0x one way and 3.0x the other, and that split has bought nothing: the
// tree is a level deeper and no lead has been re-ranked. What a split is for is
// pulling its two children APART, so that is what the badge measures.
//
// The quantity is the ratio of the better child's conversion rate to the worse
// child's. A ratio, not a difference, because the same 5 point gap means very
// different things at 2% and at 40%, and because every other multiple on this
// page (leaf lift, the node panel, the AI summary) is already read as "x times".
//
// Both children's population and conversions are read off the tree structure
// itself, never derived by summing anything. `components/insights/bucketStats.ts`
// documents what summing costs: buckets that overlap produced a baseline
// belonging to no node at all. `assembleTreeStructure` puts the real figures on
// every node, so there is nothing to reconstruct.
// ---------------------------------------------------------------------------

/**
 * A child under this many rows has a conversion rate that is mostly noise, so a
 * ratio built on it is not evidence of anything. Re-exported from the insights
 * module rather than redeclared, so the tree cannot start disagreeing with the
 * panel that proposes its splits.
 */
export { MIN_SPLIT_POPULATION };

/**
 * Ceiling on the ratio, and the answer to the divide-by-zero.
 *
 * A child with zero conversions makes the true ratio infinite, and the tree does
 * contain such nodes. Rendering nothing there would hide the strongest cuts in
 * the tree, which is the opposite of what the badge is for, so the ratio is
 * capped and formatted with a trailing "+" to say the real number is larger.
 * 20x is far past any threshold that changes a reading, so the cap never costs a
 * decision.
 */
export const SEPARATION_CAP = 20;

/**
 * At or above this, the split is drawn as earning its place; below it the badge
 * greys out.
 *
 * 2x, for the same reason 2x is the number people already read off a leaf badge
 * on this canvas. The parent's rate is the population-weighted average of its
 * two children, so a 2x gap means the split moved one side clearly above the
 * parent and the other clearly below: the leads have been re-ranked relative to
 * each other. Under 2x the two children routinely land in the same fit band once
 * the scores are tiered, which makes the node a level of depth that changes no
 * lead's grade. The exact figure is a judgement call, not a derivation, which is
 * why it lives here as one named constant rather than inline.
 */
export const STRONG_SEPARATION = 2;

export interface SplitSeparation {
  /** Higher child rate over lower child rate. Never Infinity, never NaN. */
  ratio: number;
  /** The true ratio is above `SEPARATION_CAP`, because a child converts at zero. */
  capped: boolean;
  /** Both children clear `MIN_SPLIT_POPULATION`, so the ratio is not noise. */
  reliable: boolean;
  /** Reliable and at or above `STRONG_SEPARATION`. Drives the highlight. */
  strong: boolean;
}

/**
 * How far apart a split pulls its two children. Null when the question does not
 * apply, so callers render nothing rather than a made up multiple:
 *
 * - a leaf, or any node without exactly two children. Trees here are binary, but
 *   a one-sided or collapsed node would otherwise produce a ratio against
 *   nothing.
 * - a child with no population at all, whose rate does not exist.
 *
 * Two cases return a number rather than null, deliberately:
 *
 * - both children converting at zero is 0/0. It is not "unknown", it is a split
 *   that separated nothing, so it reports 1x and greys out.
 * - one child converting at zero is the capped case above.
 */
export function splitSeparation(
  children?: { population?: number; conversions?: number }[]
): SplitSeparation | null {
  if (!children || children.length !== 2) return null;

  const sides = children.map((child) => {
    const population = Number(child?.population);
    const conversions = Number(child?.conversions);
    if (!Number.isFinite(population) || population <= 0) return null;
    if (!Number.isFinite(conversions) || conversions < 0) return null;
    return { population, rate: conversions / population };
  });
  if (sides.some((side) => side === null)) return null;
  const [left, right] = sides as { population: number; rate: number }[];

  const high = Math.max(left.rate, right.rate);
  const low = Math.min(left.rate, right.rate);
  const reliable =
    Math.min(left.population, right.population) >= MIN_SPLIT_POPULATION;

  // Neither side converts. The cut is real but it separated nothing.
  if (high <= 0) return { ratio: 1, capped: false, reliable, strong: false };

  const raw = low > 0 ? high / low : Infinity;
  const capped = !Number.isFinite(raw) || raw > SEPARATION_CAP;
  const ratio = capped ? SEPARATION_CAP : raw;
  return {
    ratio,
    capped,
    reliable,
    // A small child can throw up a huge ratio out of a handful of rows, so the
    // population floor gates the highlight as well as the number.
    strong: reliable && ratio >= STRONG_SEPARATION,
  };
}

/** "5.8x apart", or "20x+ apart" when a child converts at zero. */
export function formatSeparation(separation: SplitSeparation | null): string {
  if (!separation) return '';
  return separation.capped
    ? `${SEPARATION_CAP}x+ apart`
    : `${separation.ratio.toFixed(1)}x apart`;
}

/**
 * The chip on a split card, in words rather than a multiple.
 *
 * A leaf badge already reads "2.2x", meaning how that group converts against the
 * model average. A split showing "2.3x" means something else entirely, how far its
 * two children sit apart, and the two sit inches from each other on one canvas.
 * Two different quantities wearing one notation is a reading somebody has to be
 * taught, so the split stops using the notation. The exact ratio stays in the
 * tooltip for anyone comparing two splits precisely.
 */
/**
 * Which of the three readings this is, for colour.
 *
 * Named rather than derived from `strong` at the call site, because the wording and
 * the colour have to agree: a chip reading "splits well" in the same amber the page
 * uses for a warning says two opposite things at once.
 */
export function separationTone(
  separation: SplitSeparation | null
): 'good' | 'weak' | 'unknown' {
  if (!separation) return 'unknown';
  if (!separation.reliable) return 'unknown';
  return separation.strong ? 'good' : 'weak';
}

export function separationLabel(separation: SplitSeparation | null): string {
  if (!separation) return '';
  // The population floor answers first. A huge ratio off a handful of rows is not
  // a strong split, and saying so is more useful than either verdict.
  if (!separation.reliable) return 'too small to tell';
  return separation.strong ? 'splits well' : 'barely splits';
}

/** Why the badge reads the way it does, for the card's tooltip. */
export function separationTitle(separation: SplitSeparation | null): string {
  if (!separation) return '';
  const head = separation.capped
    ? `One side of this split converts at zero, so the two children are more than ${SEPARATION_CAP}x apart.`
    : `The better child converts ${separation.ratio.toFixed(
        1
      )}x the rate of the worse one.`;
  if (!separation.reliable) {
    return `${head} One side holds fewer than ${MIN_SPLIT_POPULATION} leads, so its rate is mostly noise and this split is not called strong on it.`;
  }
  return separation.strong
    ? `${head} At or above ${STRONG_SEPARATION}x the cut is separating leads rather than just adding depth.`
    : `${head} Under ${STRONG_SEPARATION}x the two sides rank almost the same, so this split adds depth without separating much.`;
}
